export const BLOCKED_MENU_SKUS = new Set([
  "3263467", // Friendship Day Drink
  "3263466", // Friendship Day Postcard
]);

export const normalizeSku = (value) => String(value ?? "").trim();

export const isBlockedMenuSku = (value) => BLOCKED_MENU_SKUS.has(normalizeSku(value));

const filterOptionSets = (optionSets = []) =>
  (Array.isArray(optionSets) ? optionSets : [])
    .map((group) => {
      const options = (Array.isArray(group?.options) ? group.options : [])
        .filter((option) => !isBlockedMenuSku(option?.skuCode))
        .map((option) => ({
          ...option,
          optionSets: filterOptionSets(option?.optionSets),
        }))
        .filter((option) =>
          (option.optionSets || []).every(
            (nestedGroup) => Number(nestedGroup?.min || 0) === 0 || nestedGroup.options.length > 0
          )
        );

      return { ...group, options };
    })
    .filter((group) => group.options.length > 0 || Number(group?.min || 0) > 0);

const filterProduct = (product) => {
  if (!product) return null;

  const originalVariations = Array.isArray(product.variations) ? product.variations : [];
  const hadVariantGroup = originalVariations.some((group) => group?.isVariantGroup);
  const variations = filterOptionSets(originalVariations);
  const variantGroup = variations.find((group) => group?.isVariantGroup);

  // Simple products use their own SKU. Group products inherit a variant SKU,
  // so they are retained as long as at least one allowed variant remains.
  if (!hadVariantGroup && isBlockedMenuSku(product.skuCode)) return null;
  if (hadVariantGroup && (!variantGroup || variantGroup.options.length === 0)) return null;

  const hasEmptyRequiredOptions = variations.some(
    (group) => !group?.isVariantGroup && Number(group?.min || 0) > 0 && group.options.length === 0
  );
  if (hasEmptyRequiredOptions) return null;

  if (hadVariantGroup) {
    const firstAllowedVariant = variantGroup.options[0];
    return {
      ...product,
      skuCode: isBlockedMenuSku(product.skuCode)
        ? firstAllowedVariant.skuCode
        : product.skuCode,
      price: Math.min(...variantGroup.options.map((option) => Number(option.price) || 0)),
      taxTypeIds: isBlockedMenuSku(product.skuCode)
        ? firstAllowedVariant.taxTypeIds || product.taxTypeIds
        : product.taxTypeIds,
      isPriceIncludesTax:
        isBlockedMenuSku(product.skuCode)
          ? firstAllowedVariant.isPriceIncludesTax ?? product.isPriceIncludesTax
          : product.isPriceIncludesTax,
      hasMultipleVariants: variantGroup.options.length > 1,
      variations,
    };
  }

  return {
    ...product,
    hasMultipleVariants: variantGroup
      ? variantGroup.options.length > 1
      : product.hasMultipleVariants,
    variations,
  };
};

const collectUsedTagIds = (categories) => {
  const ids = new Set();
  const collectOptions = (groups = []) => {
    groups.forEach((group) => {
      (group.options || []).forEach((option) => {
        (option.tags || []).forEach((tag) => ids.add(tag.id));
        collectOptions(option.optionSets || []);
      });
    });
  };

  categories.forEach((category) => {
    category.products.forEach((product) => {
      (product.tags || []).forEach((tag) => ids.add(tag.id));
      collectOptions(product.variations || []);
    });
  });
  return ids;
};

export function filterBlockedSkusFromMenu(menu = {}) {
  const categories = (Array.isArray(menu.categories) ? menu.categories : [])
    .map((category) => {
      const products = (Array.isArray(category?.products) ? category.products : [])
        .map(filterProduct)
        .filter(Boolean);
      const usedSubcategoryNames = new Set(
        products.map((product) => product.subCategoryName).filter(Boolean)
      );
      const subCategories = (Array.isArray(category?.subCategories)
        ? category.subCategories
        : []
      ).filter((subcategory) => usedSubcategoryNames.has(subcategory?.name));

      return { ...category, products, subCategories };
    })
    .filter((category) => category.products.length > 0);

  const usedTagIds = collectUsedTagIds(categories);
  const tags = (Array.isArray(menu.tags) ? menu.tags : []).filter((tag) =>
    usedTagIds.has(tag?.id)
  );
  const productNames = [
    ...new Set(
      categories.flatMap((category) =>
        category.products
          .map((product) => String(product?.name || "").trim().toLowerCase())
          .filter(Boolean)
      )
    ),
  ];

  return { ...menu, categories, tags, productNames };
}

export function hasBlockedCartSelection(product, variant = null, selectedOptions = []) {
  return (
    isBlockedMenuSku(product?.skuCode) ||
    isBlockedMenuSku(variant?.skuCode) ||
    (Array.isArray(selectedOptions) ? selectedOptions : []).some((option) =>
      isBlockedMenuSku(option?.skuCode)
    )
  );
}

export function blockedSkuUnavailableMap() {
  return [...BLOCKED_MENU_SKUS].reduce((lookup, skuCode) => {
    lookup[skuCode] = { skuCode, shortName: "Unavailable item" };
    return lookup;
  }, {});
}
